import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';

interface SplatConfig {
  jsonPath?: string;
  position?: number[];
  rotation?: number[];
  scale?: number[];
  renderCallback?: () => void;
  onProgress?: (progress: number) => void;
  cuttingInfoPath?: string;
  url?: string;
  avatarInfo?: any;
  gaussianSplats3DOption?: any;
  customMesh?: boolean;
  renderOrder?: number;
  cuttingInfo?: any;
  sphericalHarmonicsDegree?: any;
  showLoadingUI: boolean;
}
interface CuttingInfoConfig {
  cuttingInfo?: any;
  renderOrder?: number;
  absolutePosition?: number[];
  relativePosition?: number[];
  url?: string;
  startPoint?: number[];
}

export class SingleGS3DScene {
  viewer: any;
  controller: any;
  jsonPath?: string;
  position: number[] | null;
  config: SplatConfig | null;
  loadedChunks: Map<any, any>; // 存储已加载的切片
  loadingChunks: Set<any>; // 正在加载的切片
  maxLoadDistance: number; // 最大加载距离（切片单位）

  constructor(controller: any, options: SplatConfig) {
    this.controller = controller;
    this.jsonPath = options.jsonPath;
    this.position = options.position || null;
    this.loadedChunks = new Map();
    this.loadingChunks = new Set();
    this.maxLoadDistance = 6;
    this.config = null;

    this.getConfig(options).then((config: SplatConfig) => {
      this.config = config;
      // 如果场景单独设置了customMess, 则加载mesh材质
      this.config.customMesh && this.controller.loadMesh(this.config);
      // 检查是否是分块加载配置 如果有，则按照分块来加载
      if (this.config.cuttingInfo) {
        // this.renderChunkedSplats();
        return;
      }

      this.loadSplatFile(this.config).then(() => {
        this.config?.renderCallback?.();
      });
    });
  }

  /**
   * 根据切片索引计算在paths数组中的位置
   */
  getPathIndexFromChunkIndex(chunkIndex: { x: number; y: number; z: number }, numX: number, numY: number): number {
    return chunkIndex.z * numX * numY + chunkIndex.y * numX + chunkIndex.x;
  }

  async loadSplatFile(splatConfig: SplatConfig): Promise<void> {
    console.log('loadSplatFile ', splatConfig);
    this.viewer = new GaussianSplats3D.DropInViewer({
      dynamicScene: true,
      progressiveLoad: true,
      ...splatConfig.gaussianSplats3DOption,
    });
    await this.viewer.addSplatScene(splatConfig.url, {
      progressiveLoad: true,
      position: splatConfig.position,
      rotation: splatConfig.rotation,
      scale: splatConfig.scale,
      showLoadingUI: false,
      progressiveRenderDelay: 1000,
      maxPendingChunkSize: 32768,
      onProgress: splatConfig.onProgress,
    });

    // 设置渲染顺序，数值越小越先渲染（在后面）
    // 如果是后续添加的场景，设置更小的renderOrder值
    if (splatConfig.renderOrder !== undefined) {
      this.viewer.renderOrder = splatConfig.renderOrder;

      // 备选方案：通过调整Z轴位置控制深度
      // 负的renderOrder表示需要向后移动
      if (splatConfig.renderOrder < 0) {
        this.viewer.position.z += splatConfig.renderOrder * 0.01; // 微调Z轴位置
      }
    } else {
      // 默认情况下，后添加的场景使用更小的renderOrder
      this.viewer.renderOrder = 0;
    }

    this.controller.scene.add(this.viewer);
  }

  async getCuttingInfoConfig(url: string): Promise<CuttingInfoConfig[]> {
    const requestFile = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'reload',
    });
    if (requestFile.status !== 200) {
      throw new Error(`load ${url} error ${requestFile.statusText},${requestFile.body}`);
    }
    const content = await requestFile.json();
    console.log(`getCuttingInfoConfig success file url ${url} content:`, content);
    return content;
  }

  async getConfig(splatConfig: SplatConfig): Promise<SplatConfig> {
    const viewerOptions = {
      sphericalHarmonicsDegree: splatConfig?.sphericalHarmonicsDegree >= 0 ? splatConfig.sphericalHarmonicsDegree : 2,
      sharedMemoryForWorkers: false,
      freeIntermediateSplatData: true,
      sceneRevealMode: GaussianSplats3D.SceneRevealMode.Gradual,
      useBuiltInControls: false,
      // @ts-ignore
      threeScene: this.scene,
      selfDrivenMode: false,
      ...splatConfig?.gaussianSplats3DOption,
    };

    const config: SplatConfig = {
      avatarInfo: splatConfig.avatarInfo,
      position: splatConfig.position,
      rotation: splatConfig.rotation,
      scale: splatConfig.scale,
      renderCallback: splatConfig.renderCallback,
      showLoadingUI: false,
      onProgress: splatConfig.onProgress,
      gaussianSplats3DOption: viewerOptions,
    };
    // 如果是切片的
    if (splatConfig.cuttingInfoPath) {
      const cuttingInfoConfigs = await this.getCuttingInfoConfig(splatConfig.cuttingInfoPath);
      const cuttingInfoConfig = this.getCuttingInfo(cuttingInfoConfigs, splatConfig.avatarInfo?.position);
      cuttingInfoConfig.cuttingInfo && (config.cuttingInfo = cuttingInfoConfig.cuttingInfo);
      Number.isInteger(cuttingInfoConfig?.renderOrder) && (config.renderOrder = cuttingInfoConfig.renderOrder);
      if (cuttingInfoConfig.absolutePosition) {
        config.position = cuttingInfoConfig.absolutePosition;
      }
      if (cuttingInfoConfig.relativePosition) {
        const [x, y, z] = config.position!;
        const [rx, ry, rz] = cuttingInfoConfig.relativePosition;
        config.position = [x + rx, y + ry, z + rz];
      }
      cuttingInfoConfig.url && (config.url = cuttingInfoConfig.url);
    } else if (splatConfig.url) {
      config.url = splatConfig.url;
    }
    return config;
  }

  getCuttingInfo(configs: CuttingInfoConfig[], position?: number[]): CuttingInfoConfig {
    if (!(configs instanceof Array)) {
      return configs;
    }
    if (!position) {
      return configs[0];
    }

    // 计算与给定position距离最近的配置项
    let minDistance = 99999;
    let closestConfig = configs[0];

    for (const config of configs) {
      if (!config.startPoint || !Array.isArray(config.startPoint)) {
        continue;
      }

      // 计算3D欧几里得距离
      const dx = position[0] - config.startPoint[0];
      const dy = position[1] - config.startPoint[1];
      const dz = position[2] - config.startPoint[2];
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (distance < minDistance) {
        minDistance = distance;
        closestConfig = config;
      }
    }

    return closestConfig;
  }

  /**
   * 卸载整个SingleScene实例，清理所有资源
   */
  dispose(): void {
    console.log('开始卸载 SingleScene...');
    this.controller.scene.remove(this.viewer);
    delete this.viewer;
  }
}
